import { t as translate } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { AvatarUploadField } from '@/components/atoms/AvatarUploadField/AvatarUploadField';
import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { LanguageFlag } from '@/components/atoms/FlagEmoji/LanguageFlag';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { usePreferences } from '@/hooks/usePreferences';
import { useRegisterSectionSave } from '@/hooks/useRegisterSectionSave';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import useUser from '@/hooks/useUser';
import { UserPreferences } from '@/shared/types/user';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import { timezones } from '@/util/timezones';
import { LENGTH_S } from '@/util/validation/constants';
import { noUrlPattern } from '@/util/validation/patterns';

const FORM_ID = 'profile-info-form';

const timezoneOptions = timezones.map((tz) => ({ value: tz, label: tz }));

type Form = { name: string; language: string; timezone: string };

const profileSchema = yup.object({
  name: yup
    .string()
    .required(() => translate('validation.required'))
    .max(LENGTH_S, () => translate('validation.less_than', { 0: LENGTH_S }))
    .test(
      'no-url',
      () => translate('validation.url_not_allowed'),
      (value) => !noUrlPattern.test(value ?? '')
    ),
  language: yup.string().required(() => translate('validation.required')),
  timezone: yup.string().required(() => translate('validation.required')),
});

export const ProfileSection = () => {
  const { t } = useTranslation();
  const { user, updateUser, updateUserAsync, supportedLanguageOptions } = useUser();
  const { userPreferences, updateUserPreferencesAsync } = usePreferences();

  const languageOptions = supportedLanguageOptions.map((option) => ({
    ...option,
    icon: (
      <span aria-hidden="true" className="flex w-4 shrink-0 items-center">
        <LanguageFlag code={option.value} />
      </span>
    ),
  }));

  const storedTimezone =
    typeof userPreferences?.timezone === 'string' ? userPreferences.timezone : '';

  const form = useForm<Form>({
    mode: 'onChange',
    resolver: createYupResolver<Form>(profileSchema),
    resetOptions: KEEP_DIRTY_VALUES,
    values: {
      name: user?.name ?? '',
      language: userPreferences?.language ? String(userPreferences.language) : '',
      timezone: timezones.includes(storedTimezone) ? storedTimezone : '',
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting, dirtyFields },
  } = form;

  useTrackFormState({ isDirty, formId: FORM_ID });

  const onSubmit = async (data: Form) => {
    const tasks: Promise<unknown>[] = [];
    if (user && dirtyFields.name) {
      tasks.push(updateUserAsync({ name: data.name.trim() }));
    }
    const prefs: Partial<UserPreferences> = {};
    if (dirtyFields.language) {
      prefs.language = data.language;
    }
    if (dirtyFields.timezone) {
      prefs.timezone = data.timezone;
    }
    if (Object.keys(prefs).length > 0) {
      tasks.push(updateUserPreferencesAsync(prefs));
    }
    await Promise.all(tasks);
    form.reset(data, CLEAR_DIRTY_VALUES);
  };

  useRegisterSectionSave(FORM_ID, isDirty, handleSubmit, onSubmit);

  return (
    <SectionLayout title={t('pages.profile')}>
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={isSubmitting}
            disabled={!isDirty}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="space-y-4">
              <AvatarUploadField
                id="avatar-upload"
                name={user?.name}
                avatar={user?.avatar}
                isUserAvatar
                onUpdate={updateUser}
                label={t('profile.change_picture')}
                recommendedHint={t('profile.avatar_recommended')}
                supportedHint={t('profile.avatar_supported')}
              />

              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('field.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={control}
                  name="language"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('field.language')}</FormLabel>
                      <FormControl>
                        <PopoverCombobox
                          options={languageOptions}
                          value={field.value}
                          onChange={field.onChange}
                          error={!!fieldState.error}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="timezone"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('field.timezone')}</FormLabel>
                      <FormControl>
                        <PopoverCombobox
                          options={timezoneOptions}
                          value={field.value}
                          onChange={field.onChange}
                          error={!!fieldState.error}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </SectionLayout>
  );
};
